import { Component, DestroyRef, inject, OnInit, signal, viewChild } from '@angular/core';
import { BrandCardComponent } from '../brand-card/brand-card.component';
import { TranslatePipe } from '@ngx-translate/core';
import { KorviuListComponent, KorviuLoaderComponent } from 'shared';
import { finalize, Observable } from 'rxjs';
import { ICategory } from 'core/types/category.interface';
import { CategoriesService } from 'core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IKorviuListConfig, KorviuListItemTypes } from 'shared/types/korviu-list.config';
import { IPagination } from 'shared/types/pagination.interface';
import { IFilter } from 'shared/types/filter.interface';
import { ISorting } from 'shared/types/sorting.interface';
import { IGetPaginatedResponse } from 'shared/types/get-paginated-response.interface';
import { IProduct } from 'core/types/product.interface';
import { IBrand } from 'shared/types/brand.interface';
import { ActivatedRoute, Router } from '@angular/router';
import { CategoryCarouselComponent } from '../category-carousel/category-carousel.component';
import { SortOrders } from 'shared/types/sort-order.enum';
import { ListFiltersTemplateDirective } from 'shared/directives/list-filters.directive';

type RewardQuickFilterKey = 'new' | 'usa' | 'mx';

interface IRewardQuickFilter {
  key: RewardQuickFilterKey;
  localizableName: string;
  query: IFilter;
}

@Component({
  selector: 'app-explore',
  imports: [
    BrandCardComponent,
    TranslatePipe,
    KorviuLoaderComponent,
    KorviuListComponent,
    CategoryCarouselComponent,
    ListFiltersTemplateDirective
  ],
  templateUrl: './explore.component.html',
  styleUrl: './explore.component.scss'
})
export class ExploreComponent implements OnInit {
  readonly productList = viewChild<KorviuListComponent>('productList');

  readonly #categoriesService = inject(CategoriesService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #router = inject(Router);
  readonly #activatedRoute = inject(ActivatedRoute);

  readonly categoriesIsLoading = signal(false);
  readonly selectedCategoryId = signal<number | null>(null);
  readonly categories = signal<ICategory[]>([]);

  readonly brandsIsLoading = signal(false);
  readonly brands = signal<IBrand[]>([]);

  readonly activeRewardFilterKeys = signal<ReadonlySet<RewardQuickFilterKey>>(new Set<RewardQuickFilterKey>());
  readonly rewardFilters: readonly IRewardQuickFilter[] = [
    {
      key: 'new',
      localizableName: 'rewards.filterNew',
      query: { name: 'label', value: 'new' }
    },
    {
      key: 'usa',
      localizableName: 'rewards.availableInUs',
      query: { name: 'redeemable_in', value: 'usa' }
    },
    {
      key: 'mx',
      localizableName: 'rewards.availableInMx',
      query: { name: 'redeemable_in', value: 'mx' }
    }
  ];

  readonly productListConfig: IKorviuListConfig = {
    getFn: this.getProducts.bind(this),
    sortSections: [
      {
        localizableName: 'rewards.brand',
        options: [
          {
            localizableName: 'common.sortOption.A-Z',
            sortDirection: { sortBy: 'brand__name', sortOrder: SortOrders.ASCEND }
          },
          {
            localizableName: 'common.sortOption.Z-A',
            sortDirection: { sortBy: 'brand__name', sortOrder: SortOrders.DESCEND }
          }
        ]
      },
      {
        localizableName: 'rewards.ganasAmount',
        options: [
          {
            localizableName: 'common.sortOption.lowest',
            sortDirection: { sortBy: 'total', sortOrder: SortOrders.ASCEND }
          },
          {
            localizableName: 'common.sortOption.highest',
            sortDirection: { sortBy: 'total', sortOrder: SortOrders.DESCEND }
          }
        ]
      }
    ],
    filterSections: [],
    hideFilterSection: true,
    itemType: KorviuListItemTypes.PRODUCT_LIST,
    totalLocalizableName: 'rewards.rewards',
    pagination: { pageSize: 12, page: 1 }
  };

  ngOnInit(): void {
    this.#loadCategories();
    this.#loadBrands();
  }

  getProducts(
    pagination: IPagination,
    filters?: IFilter[],
    sorting?: ISorting
  ): Observable<IGetPaginatedResponse<IProduct[]>> {
    const categoryId = this.selectedCategoryId();
    const activeRewardFilterKeys = this.activeRewardFilterKeys();
    const requestFilters: IFilter[] = [
      ...(filters ?? []),
      ...this.rewardFilters
        .filter(filter => activeRewardFilterKeys.has(filter.key))
        .map(filter => ({ ...filter.query }))
    ];

    if (sorting) {
      requestFilters.push({
        name: 'sort_by',
        value: `${sorting.sortOrder === SortOrders.DESCEND ? '-' : ''}${sorting.sortBy}`
      });
    }

    return this.#categoriesService.getCategoryProducts(categoryId, pagination, requestFilters);
  }

  onCategoryClick(categoryId: number | null): void {
    this.selectedCategoryId.set(categoryId);
    this.productList()?.reloadData(true);
  }

  toggleRewardFilter(filterKey: RewardQuickFilterKey): void {
    this.activeRewardFilterKeys.update(activeFilterKeys => {
      const updatedFilterKeys = new Set(activeFilterKeys);

      if (updatedFilterKeys.has(filterKey)) {
        updatedFilterKeys.delete(filterKey);
      } else {
        updatedFilterKeys.add(filterKey);
      }

      return updatedFilterKeys;
    });
    this.productList()?.reloadData(true);
  }

  onViewAllBrands(): void {
    this.#router.navigate(['brands'], { relativeTo: this.#activatedRoute.parent });
  }

  onBrandClick(brandId: number): void {
    this.#router.navigate(['brands', brandId], { relativeTo: this.#activatedRoute.parent });
  }

  #loadBrands(): void {
    this.brandsIsLoading.set(true);
    this.#categoriesService
      .getAllBrandsV2({ page: 1, pageSize: 5 })
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((brands: IGetPaginatedResponse<IBrand[]>) => {
        this.brands.set(brands.results);
        this.brandsIsLoading.set(false);
      });
  }

  #loadCategories(): void {
    this.categoriesIsLoading.set(true);
    this.#categoriesService
      .getCategoriesList()
      .pipe(
        finalize(() => {
          this.categoriesIsLoading.set(false);
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe((categories: ICategory[]) => {
        this.categories.set(categories);
      });
  }
}
